import { ArrowPathIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js'
import modelUrl from '../../assets/body_model_mesh.obj?url'

// Keypoint positions from the specification
const KEYPOINT_POSITIONS = [
  [-1.28164e-4, 4.33864e-1, 6.19239e-2],
  [-7.6661e-4, 4.03476e-1, -1.17087e-2],
  [-6.92393e-2, 3.84535e-1, -1.16839e-2],
  [-1.3444e-1, 3.79175e-1, -1.57291e-2],
  [-2.01069e-1, 3.81522e-1, -1.06743e-2],
  [-2.14349e-1, 3.8025e-1, -8.87332e-3],
  [6.87706e-2, 3.84049e-1, -1.35936e-2],
  [1.31879e-1, 3.7784e-1, -1.51981e-2],
  [1.9923e-1, 3.82525e-1, -9.93063e-3],
  [2.12258e-1, 3.80848e-1, -8.68106e-3],
  [-2.90971e-2, 1.85459e-1, -6.52432e-3],
  [-4.43836e-2, 1.05185e-1, -5.35661e-3],
  [-4.93326e-2, 9.13334e-3, -3.03236e-2],
  [-5.25665e-2, 1.47069e-3, -1.34164e-2],
  [2.68517e-2, 1.88464e-1, -7.37253e-3],
  [4.19546e-2, 1.06384e-1, -5.73786e-3],
  [4.81781e-2, 9.43194e-3, -2.92613e-2],
  [5.1579e-2, 1.67344e-3, -1.36408e-2],
  [-2.24616e-2, 4.49856e-1, 4.74868e-2],
  [2.26315e-2, 4.49492e-1, 4.66643e-2],
  [-4.55e-2, 4.42492e-1, -1.10043e-2],
  [4.55e-2, 4.42492e-1, -1.10043e-2],
]

// Keypoint labels (you can customize these based on your actual labels)
const KEYPOINT_LABELS = [
  'Nose',
  'Neck',
  'Right Shoulder',
  'Right Elbow',
  'Right Wrist',
  'Right Hand',
  'Left Shoulder',
  'Left Elbow',
  'Left Wrist',
  'Left Hand',
  'Right Hip',
  'Right Knee',
  'Right Ankle',
  'Right Foot',
  'Left Hip',
  'Left Knee',
  'Left Ankle',
  'Left Foot',
  'Right Eye',
  'Left Eye',
  'Right Ear',
  'Left Ear',
]

export default function Mesh3DViewer() {
  const mountRef = useRef<HTMLDivElement>(null)
  const sceneRef = useRef<THREE.Scene | null>(null)
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null)
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null)
  const controlsRef = useRef<OrbitControls | null>(null)
  const frameRef = useRef<number | null>(null)
  const labelGroupRef = useRef<THREE.Group | null>(null)
  const keypointGroupRef = useRef<THREE.Group | null>(null)
  const raycasterRef = useRef<THREE.Raycaster | null>(null)
  const mouseRef = useRef<THREE.Vector2>(new THREE.Vector2())

  const [showLabels, setShowLabels] = useState(true)
  const navigate = useNavigate()

  const resetView = () => {
    if (cameraRef.current && controlsRef.current) {
      // Reset camera position
      cameraRef.current.position.set(0, 0.2, 1.5)
      // Reset controls target
      controlsRef.current.target.set(0, 0, 0)
      controlsRef.current.update()
    }
  }

  const toggleLabels = () => {
    setShowLabels(prev => !prev)
    if (labelGroupRef.current) {
      labelGroupRef.current.visible = !showLabels
    }
  }

  const handleKeypointClick = (keypointIndex: number) => {
    const keypointName = KEYPOINT_LABELS[keypointIndex].toLowerCase().replace(/\s+/g, '-')
    navigate(`/annotation-guide/${keypointName}`)
  }

  const onMouseClick = (event: MouseEvent) => {
    if (!mountRef.current || !cameraRef.current || !raycasterRef.current) return

    // Prevent navigation when using controls
    if (controlsRef.current && controlsRef.current.enabled === false) return

    const rect = mountRef.current.getBoundingClientRect()
    mouseRef.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1
    mouseRef.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1

    raycasterRef.current.setFromCamera(mouseRef.current, cameraRef.current)

    const intersectableObjects: THREE.Object3D[] = []

    // Add keypoint spheres to intersectable objects
    if (keypointGroupRef.current) {
      keypointGroupRef.current.children.forEach(child => {
        intersectableObjects.push(child)
      })
    }

    // Add label sprites to intersectable objects
    if (labelGroupRef.current && showLabels) {
      labelGroupRef.current.children.forEach(child => {
        intersectableObjects.push(child)
      })
    }

    const intersects = raycasterRef.current.intersectObjects(intersectableObjects)

    if (intersects.length > 0) {
      // Find which keypoint was clicked
      const clickedObject = intersects[0].object
      let keypointIndex = -1

      if (keypointGroupRef.current && keypointGroupRef.current.children.includes(clickedObject)) {
        keypointIndex = keypointGroupRef.current.children.indexOf(clickedObject)
      } else if (labelGroupRef.current && labelGroupRef.current.children.includes(clickedObject)) {
        keypointIndex = labelGroupRef.current.children.indexOf(clickedObject)
      }

      if (keypointIndex >= 0) {
        handleKeypointClick(keypointIndex)
      }
    }
  }

  useEffect(() => {
    if (!mountRef.current) return

    // Scene setup
    const scene = new THREE.Scene()
    scene.background = new THREE.Color(0xf5f5f5)
    sceneRef.current = scene

    // Camera setup
    const camera = new THREE.PerspectiveCamera(
      50,
      mountRef.current.clientWidth / mountRef.current.clientHeight,
      0.1,
      1000
    )
    camera.position.set(0, 0.2, 1.5)
    cameraRef.current = camera

    // Renderer setup
    const renderer = new THREE.WebGLRenderer({ antialias: true })
    renderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight)
    renderer.setPixelRatio(window.devicePixelRatio)
    mountRef.current.appendChild(renderer.domElement)
    rendererRef.current = renderer

    // Raycaster setup
    const raycaster = new THREE.Raycaster()
    raycasterRef.current = raycaster

    // Controls setup
    const controls = new OrbitControls(camera, renderer.domElement)
    controls.enableDamping = true
    controls.dampingFactor = 0.05
    controls.screenSpacePanning = true // Enable screen space panning
    controls.minDistance = 0.5
    controls.maxDistance = 5
    controls.enablePan = true // Enable pan
    controls.panSpeed = 1.0 // Pan speed
    controlsRef.current = controls

    // Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6)
    scene.add(ambientLight)

    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8)
    directionalLight.position.set(1, 1, 0.5)
    scene.add(directionalLight)

    const directionalLight2 = new THREE.DirectionalLight(0xffffff, 0.4)
    directionalLight2.position.set(-1, -1, -0.5)
    scene.add(directionalLight2)

    // Load OBJ model
    const loader = new OBJLoader()
    loader.load(
      modelUrl,
      (object: THREE.Group) => {
        // Center and scale the model
        const box = new THREE.Box3().setFromObject(object)
        const originalCenter = box.getCenter(new THREE.Vector3())
        const size = box.getSize(new THREE.Vector3())

        const maxDim = Math.max(size.x, size.y, size.z)
        const scale = 1.2 / maxDim

        // Store the scaled center for use with keypoints
        const scaledCenter = originalCenter.clone().multiplyScalar(scale)

        object.scale.multiplyScalar(scale)
        object.position.sub(scaledCenter)

        // Apply material
        object.traverse((child: THREE.Object3D) => {
          if (child instanceof THREE.Mesh) {
            child.material = new THREE.MeshPhongMaterial({
              color: 0xff6b6b, // Red color
              specular: 0x111111,
              shininess: 30,
              side: THREE.DoubleSide,
              transparent: true,
              opacity: 0.4, // 40% opacity
              depthWrite: false, // Ensure proper transparency rendering
            })
          }
        })

        scene.add(object)

        // Add keypoints
        const keypointGroup = new THREE.Group()
        const labelGroup = new THREE.Group()
        keypointGroupRef.current = keypointGroup
        labelGroupRef.current = labelGroup

        KEYPOINT_POSITIONS.forEach((position, index) => {
          // Create sphere for keypoint
          const geometry = new THREE.SphereGeometry(0.01, 16, 16)
          const material = new THREE.MeshBasicMaterial({
            color: new THREE.Color(`hsl(${(index * 360) / KEYPOINT_POSITIONS.length}, 70%, 50%)`),
          })
          const sphere = new THREE.Mesh(geometry, material)

          // Apply the same transformation as the model
          const keypointPos = new THREE.Vector3(position[0], position[1], position[2])
          keypointPos.multiplyScalar(scale)
          keypointPos.sub(scaledCenter)
          sphere.position.copy(keypointPos)

          keypointGroup.add(sphere)

          // Create label sprite
          const canvas = document.createElement('canvas')
          const context = canvas.getContext('2d')!
          canvas.width = 256
          canvas.height = 64

          context.fillStyle = 'white'
          context.fillRect(0, 0, 256, 64)

          context.font = 'Bold 24px Arial'
          context.fillStyle = 'black'
          context.textAlign = 'center'
          context.fillText(KEYPOINT_LABELS[index], 128, 40)

          const texture = new THREE.CanvasTexture(canvas)
          const spriteMaterial = new THREE.SpriteMaterial({ map: texture })
          const sprite = new THREE.Sprite(spriteMaterial)
          sprite.scale.set(0.1, 0.025, 1)

          // Apply the same transformation as the sphere, with slight offset for label
          const labelPos = keypointPos.clone()
          labelPos.y += 0.03
          sprite.position.copy(labelPos)

          labelGroup.add(sprite)
        })

        scene.add(keypointGroup)
        scene.add(labelGroup)

        // Set initial label visibility
        labelGroup.visible = showLabels
      },
      (progress: ProgressEvent) => {
        console.log('Loading:', ((progress.loaded / progress.total) * 100).toFixed(2) + '%')
      },
      (error: unknown) => {
        console.error('Error loading OBJ:', error)
      }
    )

    // Animation loop
    const animate = () => {
      frameRef.current = requestAnimationFrame(animate)
      controls.update()
      renderer.render(scene, camera)
    }
    animate()

    // Handle resize
    const handleResize = () => {
      if (!mountRef.current) return

      camera.aspect = mountRef.current.clientWidth / mountRef.current.clientHeight
      camera.updateProjectionMatrix()
      renderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight)
    }
    window.addEventListener('resize', handleResize)

    // Add click event listener
    renderer.domElement.addEventListener('click', onMouseClick)

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize)
      renderer.domElement.removeEventListener('click', onMouseClick)
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current)
      }
      controls.dispose()
      renderer.dispose()
      if (mountRef.current && renderer.domElement) {
        mountRef.current.removeChild(renderer.domElement)
      }
    }
  }, [showLabels])

  return (
    <div className="relative w-full h-full">
      <div ref={mountRef} className="w-full h-full cursor-pointer" />
      <div className="absolute top-4 right-4 flex flex-col gap-2">
        <button
          onClick={resetView}
          className="bg-white hover:bg-gray-100 text-gray-700 font-medium py-2 px-3 rounded-lg shadow-md flex items-center gap-2 transition-colors"
          title="Reset View"
        >
          <ArrowPathIcon className="w-4 h-4" />
          Reset View
        </button>
        <button
          onClick={toggleLabels}
          className="bg-white hover:bg-gray-100 text-gray-700 font-medium py-2 px-3 rounded-lg shadow-md flex items-center gap-2 transition-colors"
          title={showLabels ? 'Hide Labels' : 'Show Labels'}
        >
          {showLabels ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
          {showLabels ? 'Hide Labels' : 'Show Labels'}
        </button>
      </div>
    </div>
  )
}
